import apiService from './api';

interface EmailSubscriber {
  id: number;
  email: string;
  subscriptionTier: string;
  subscriptionStatus: string;
  alertThreshold: number;
  categories: string[];
  digestType: string;
  language: string;
  isActive: boolean;
  isVerified: boolean;
  lastEmailSent?: string;
  emailCount: number;
  createdAt: string;
}

interface EmailTemplate {
  id: number;
  templateType: string;
  language: string;
  subject: string;
  htmlContent: string;
  textContent: string;
  availableVariables: string[];
  isActive: boolean;
  version: string;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

interface EmailLog {
  id: number;
  subscriberId: number;
  templateId: number;
  emailType: string;
  subject: string;
  recipientEmail: string;
  status: string;
  errorMessage?: string;
  riskScore?: number;
  articleCount?: number;
  categoriesIncluded: string[];
  sentAt?: string;
  deliveredAt?: string;
  openedAt?: string;
  clickedAt?: string;
}

interface EmailStats {
  subscribers: {
    total: number;
    active: number;
    verified: number;
  };
  emails: {
    sent24h: number;
    sent7d: number;
    delivered: number;
    opened: number;
  };
  templates: {
    total: number;
    active: number;
  };
  categories: Array<{
    code: string;
    name: string;
    subscribers: number;
  }>;
}

interface QueueStatus {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
}

interface SubscriberFilters {
  isActive?: string;
  digestType?: string;
  category?: string;
  page?: string;
  limit?: string;
}

interface LogFilters {
  status?: string;
  templateType?: string;
  startDate?: string;
  endDate?: string;
  page?: string;
  limit?: string;
}

class EmailApiService {

  // Subscribers
  async getSubscribers(filters: SubscriberFilters = {}) {
    const params = new URLSearchParams(filters as any);
    const response = await apiService.get(`/api/admin/email/subscribers?${params}`);
    return response.data;
  }

  async deleteSubscriber(id: number) {
    const response = await apiService.delete(`/api/admin/email/subscribers/${id}`);
    return response.data;
  }

  async exportSubscribers() {
    const response = await apiService.get('/api/admin/email/subscribers/export', {
      responseType: 'blob',
    });
    return response.data;
  }

  // Templates
  async getTemplates() {
    const response = await apiService.get('/api/admin/email/templates');
    return response.data;
  }

  async getTemplate(id: number) {
    const response = await apiService.get(`/api/admin/email/templates/${id}`);
    return response.data;
  }

  async updateTemplate(id: number, data: Partial<EmailTemplate>) {
    const response = await apiService.put(`/api/admin/email/templates/${id}`, data);
    return response.data;
  }

  async createTemplate(data: Partial<EmailTemplate>) {
    const response = await apiService.post('/api/admin/email/templates', data);
    return response.data;
  }

  // Logs
  async getLogs(filters: LogFilters = {}) {
    const params = new URLSearchParams(filters as any);
    const response = await apiService.get(`/api/admin/email/logs?${params}`);
    return response.data;
  }

  // Stats
  async getStats() {
    const response = await apiService.get('/api/admin/email/stats');
    return response.data;
  }

  // Queue
  async getQueueStatus() {
    const response = await apiService.get('/api/admin/email/queue');
    return response.data;
  }

  async retryQueueItem(id: number) {
    const response = await apiService.post(`/api/admin/email/queue/retry/${id}`);
    return response.data;
  }

  // Test email
  async sendTestEmail(email: string, templateType: string) {
    const response = await apiService.post('/api/admin/email/test', { email, templateType });
    return response.data;
  }

  async sendInstantTest(subscriberId: number, count: number = 3) {
    const response = await apiService.post(`/api/admin/email/test/instant/${subscriberId}`, { count });
    return response.data;
  }

  // Public endpoints (no auth required)
  async getCategories() {
    const response = await apiService.get('/api/email/categories');
    return response.data;
  }

  async subscribe(data: any) {
    const response = await apiService.post('/api/email/subscribe', data);
    return response.data;
  }
}

export const emailApi = new EmailApiService();
export default emailApi;
